import MuleBadge from '../../components/MuleBadge';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { ArrowPathIcon, ArrowTopRightOnSquareIcon, MagnifyingGlassIcon, MapPinIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../components/TanTable';
import WarningConfirmModal from '../../components/ui/WarningConfirmModal';
import { useToast } from '../../contexts/ToastContext';
import ordersService from '../../services/orders.service';
import {
  useAdditionalFieldSelection,
  useAdditionalFieldColumns,
} from '../../hooks/useAdditionalFields';
import FilterBar from '../../components/FilterBar';
import DateTimePicker from '../../components/DateTimePicker';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import SendDispatchConfirmModal from '../../components/leg/SendDispatchConfirmModal';
import TravelHistoryModal from '../../components/TravelHistoryModal';
import { DraggableModal } from '../../components/ui/DraggableModal';
import { ViewedProsProvider, useViewedPros } from '../../contexts/ViewedProsContext';
import unitsService, { DriverDispatchHistoryRow } from '../../services/units.service';
import dispatchPaperworkService from '../../services/dispatchPaperwork.service';
import printingService, { type PrintOptions } from '../../services/printing.service';
import PrintDialog from '../../components/printing/PrintDialog';
import { saveAs } from '../../utils/saveFile';
import { config } from '../../config/config';

const formatDateKey = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const parseDateKey = (key: string | null): Date | null => {
  if (!key) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

const formatDate = (value: string | null): string => {
  if (!value) return '-';
  const d = new Date(value.length === 10 ? value + 'T00:00:00' : value);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
};

const formatDateTime = (value: string | null): string => {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '-';
  // Treat the all-zero MySQL sentinel as empty
  if (d.getFullYear() < 1900) return '-';
  return d.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const formatTime = (value: string | null): string => {
  if (!value) return '';
  // value is HH:MM:SS from server
  const parts = value.split(':');
  if (parts.length < 2) return value;
  return `${parts[0]}:${parts[1]}`;
};

const todayKey = (): string => formatDateKey(new Date());

interface PriorityCellProps {
  row: DriverDispatchHistoryRow;
  onSave: (pronumber: number, legnum: number, legrecno: number, priority: number) => Promise<void>;
}

function PriorityCell({ row, onSave }: PriorityCellProps) {
  const [value, setValue] = useState<string>(row.dispatch_priority != null ? String(row.dispatch_priority) : '');
  const [saving, setSaving] = useState(false);

  const commit = async () => {
    const parsed = parseInt(value, 10);
    if (!value || isNaN(parsed) || parsed < 1 || parsed === row.dispatch_priority) return;
    setSaving(true);
    try {
      await onSave(row.pronumber, row.legnum, row.legrecno, parsed);
    } finally {
      setSaving(false);
    }
  };

  return (
    <input
      type="number"
      min={1}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      disabled={saving}
      className="w-14 px-1 py-0.5 text-sm text-center border border-gray-300 dark:border-gray-600 rounded bg-panel text-heading disabled:opacity-50"
      onClick={(e) => e.stopPropagation()}
    />
  );
}

interface DriverDispatchHistoryContentProps {
  unit: string;
  startDate: string;
  endDate: string;
  onUnitChange: (unit: string) => void;
  onStartChange: (yyyyMmDd: string) => void;
  onEndChange: (yyyyMmDd: string) => void;
}

function DriverDispatchHistoryContent({
  unit: unitParam,
  startDate: startParam,
  endDate: endParam,
  onUnitChange,
  onStartChange,
  onEndChange,
}: DriverDispatchHistoryContentProps) {
  // Any orders-scope registry field can be added as a column; the backend attaches
  // it to the dispatches this endpoint already returned, keyed on pronumber.
  const fieldSelection = useAdditionalFieldSelection('driver-dispatch-history');
  const additionalFieldsKey = fieldSelection.additionalFields.join(',');
  const fieldsReady = fieldSelection.ready;
  const { isViewed, markViewed } = useViewedPros();

  const startDate = useMemo(() => parseDateKey(startParam) ?? new Date(), [startParam]);
  const endDate = useMemo(() => parseDateKey(endParam) ?? new Date(), [endParam]);

  // Start and End are edited independently, so the user can land on an
  // out-of-order range. The server filters with a BETWEEN, which matches nothing
  // when the bounds are inverted — say so rather than showing an empty grid.
  const rangeInverted = useMemo(
    () => formatDateKey(startDate) > formatDateKey(endDate),
    [startDate, endDate]
  );

  const [data, setData] = useState<DriverDispatchHistoryRow[]>([]);
  const [isMule, setIsMule] = useState(false);
  const [driverName, setDriverName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Bumped by the Refresh button. The fetch effect keys off it, so re-running is
  // just a state change — the filters (unit/date range) stay exactly as they are.
  const [reloadToken, setReloadToken] = useState(0);

  // Local input for the unit-number picker, separated from the URL param
  // so the user can type without firing a request on every keystroke.
  const [unitInput, setUnitInput] = useState(unitParam);
  useEffect(() => { setUnitInput(unitParam); }, [unitParam]);

  const isMounted = useRef(true);
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      abortRef.current?.abort();
    };
  }, []);

  // Send-paperwork modal — one shared modal driven by the row chosen from
  // the Actions menu (so each row's icon controls the same dialog).
  const [sendModalRow, setSendModalRow] = useState<DriverDispatchHistoryRow | null>(null);
  // Travel-history modal — shows the leg's Samsara breadcrumb trail.
  const [travelHistoryRow, setTravelHistoryRow] = useState<DriverDispatchHistoryRow | null>(null);

  // Order drawer state (mirrors DispatchRemovals)
  const [selectedPro, setSelectedPro] = useState<number | null>(null);
  const [isOrderDrawerOpen, setIsOrderDrawerOpen] = useState(false);

  useEffect(() => {
    if (!unitParam) {
      setData([]);
      setDriverName(null);
      setIsMule(false);
      setError(null);
      return;
    }

    if (rangeInverted) {
      abortRef.current?.abort();
      setData([]);
      setError(null);
      setLoading(false);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // The saved selection is part of the request; going early just fetches twice.
    if (!fieldsReady) return;

    setLoading(true);
    setError(null);
    unitsService
      .getDriverDispatchHistory(
        unitParam, startParam, endParam, fieldSelection.additionalFields,
      )
      .then((res) => {
        if (!isMounted.current || controller.signal.aborted) return;
        setData(res.data.dispatches);
        setDriverName(res.data.driverName);
        setIsMule(!!res.data.isMule);
      })
      .catch((err) => {
        if (!isMounted.current || controller.signal.aborted) return;
        setError(err?.message || 'Failed to load driver dispatch history');
        setData([]);
        setDriverName(null);
        setIsMule(false);
      })
      .finally(() => {
        if (isMounted.current && !controller.signal.aborted) setLoading(false);
      });
  }, [unitParam, startParam, endParam, rangeInverted, fieldsReady, additionalFieldsKey, reloadToken]);

  const handleRefresh = useCallback(() => {
    setReloadToken((t) => t + 1);
  }, []);

  // Clearing a date falls back to today rather than being dropped: the request
  // always needs both bounds, so swallowing the clear left the box blank while
  // the filter quietly kept the old date.
  const handleStartChange = useCallback((val: Date | { date?: Date } | null | unknown) => {
    const d = (val as { date?: Date } | null)?.date ?? (val instanceof Date ? val : null);
    onStartChange(d ? formatDateKey(d) : todayKey());
  }, [onStartChange]);

  const handleEndChange = useCallback((val: Date | { date?: Date } | null | unknown) => {
    const d = (val as { date?: Date } | null)?.date ?? (val instanceof Date ? val : null);
    onEndChange(d ? formatDateKey(d) : todayKey());
  }, [onEndChange]);

  const applyUnit = useCallback(() => {
    const trimmed = unitInput.trim();
    if (trimmed === unitParam) return;
    onUnitChange(trimmed);
  }, [unitInput, unitParam, onUnitChange]);

  const openOrderDrawer = useCallback((pronumber: number) => {
    setSelectedPro(pronumber);
    setIsOrderDrawerOpen(true);
  }, []);

  const closeOrderDrawer = useCallback(() => {
    setIsOrderDrawerOpen(false);
    setSelectedPro(null);
  }, []);

  // Multi-select → Send to gwiMove. Selection is limited to legs that have a
  // legdispatch row and are not already flagged, which is exactly the set the
  // shared /orders/send-to-gwimove endpoint will act on (already-sent and
  // undispatched legs come back as "not sent" there, so offering them only
  // produces confusing partial-failure counts).
  const { showToast } = useToast();
  const [selectedRows, setSelectedRows] = useState<DriverDispatchHistoryRow[]>([]);
  const [selectionResetKey, setSelectionResetKey] = useState(0);
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const [sendingToGwiMove, setSendingToGwiMove] = useState(false);

  const canSelectRow = useCallback(
    (row: DriverDispatchHistoryRow) => row.dispatchrecno != null && row.sendtogwimove !== 'Y',
    [],
  );

  // A refresh / new unit replaces the rows, so drop any stale selection with it.
  useEffect(() => {
    setSelectedRows([]);
    setSelectionResetKey((k) => k + 1);
  }, [unitParam, startParam, endParam, reloadToken]);

  const handleConfirmSendToGwiMove = useCallback(async () => {
    setConfirmSendOpen(false);
    if (selectedRows.length === 0) return;
    setSendingToGwiMove(true);
    const items = selectedRows.map((r) => ({
      unitNumber: unitParam,
      proNumber: r.pronumber,
      legrecno: r.legrecno,
      legnum: r.legnum,
    }));
    try {
      const res = await ordersService.sendToGwiMove(items);
      if (!isMounted.current) return;
      if (res.failed > 0 && res.success === 0) {
        showToast({ type: 'error', message: res.message || 'Failed to send to gwiMove' });
        return;
      }
      // Flip the sent rows locally; a partial success still means the endpoint
      // ran, so the user gets the server's count and a refresh shows the truth.
      const sentKeys = new Set(selectedRows.map((r) => `${r.pronumber}-${r.legrecno}`));
      setData((prev) => prev.map((r) =>
        sentKeys.has(`${r.pronumber}-${r.legrecno}`) ? { ...r, sendtogwimove: 'Y' } : r,
      ));
      setSelectedRows([]);
      setSelectionResetKey((k) => k + 1);
      showToast({
        type: res.failed > 0 ? 'warning' : 'success',
        message: res.message || `Sent ${res.success} move(s) to gwiMove`,
      });
    } catch (err: any) {
      if (!isMounted.current) return;
      showToast({
        type: 'error',
        message: err?.response?.data?.detail || err?.message || 'Failed to send to gwiMove',
      });
    } finally {
      if (isMounted.current) setSendingToGwiMove(false);
    }
  }, [selectedRows, unitParam, showToast]);

  const handlePrioritySave = useCallback(async (pronumber: number, legnum: number, legrecno: number, priority: number) => {
    await unitsService.reorderDriverMoves(unitParam, [{ pronumber, legnum, priority }]);
    setData(prev => prev.map(r =>
      r.pronumber === pronumber && r.legrecno === legrecno
        ? { ...r, dispatch_priority: priority }
        : r,
    ));
  }, [unitParam]);

  const baseColumns = useMemo<ColumnDef<DriverDispatchHistoryRow>[]>(() => [
    {
      accessorKey: 'dispatch_priority',
      header: 'Priority',
      enableColumnFilter: false,
      size: 80,
      sortingFn: (a, b) => {
        const pa = a.original.dispatch_priority ?? 999;
        const pb = b.original.dispatch_priority ?? 999;
        return pa - pb;
      },
      cell: ({ row }) => {
        const r = row.original;
        if (r.isComplete) {
          return <span className="text-sm text-muted">{r.dispatch_priority ?? '-'}</span>;
        }
        return <PriorityCell row={r} onSave={handlePrioritySave} />;
      },
    },
    {
      accessorKey: 'pronumber',
      header: 'PRO #',
      filterFn: 'arrIncludes',
      cell: ({ row }) => {
        const pronumber = row.original.pronumber;
        const viewed = isViewed(pronumber);
        return (
          <div
            className={`flex items-center gap-1 px-1 rounded transition-colors duration-150 ${
              viewed ? 'bg-green-200 dark:bg-green-600/50' : ''
            }`}
            title={viewed ? 'Viewed' : undefined}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                markViewed(pronumber);
                openOrderDrawer(Number(pronumber));
              }}
              className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
            >
              {pronumber}
            </button>
            <a
              href={`${config.LEGACY_CMS_URL}/index_cms.pl?action=savecustomsearch&contlist=${pronumber}&searchtype=pronumber`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.stopPropagation();
                markViewed(pronumber);
              }}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              title="Open in CMSV1"
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            </a>
          </div>
        );
      },
    },
    {
      accessorKey: 'origApptDate',
      header: 'Appt Date',
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatDate(row.original.origApptDate),
      sortingFn: 'datetime',
    },
    {
      id: 'destApptTimeRange',
      accessorKey: 'destApptTime1',
      header: 'Delivery Time',
      filterFn: 'arrIncludes',
      cell: ({ row }) => {
        const t1 = formatTime(row.original.destApptTime1);
        const t2 = formatTime(row.original.destApptTime2);
        if (t1 && t2 && t1 !== t2) return `${t1} - ${t2}`;
        return t1 || '-';
      },
    },
    {
      accessorKey: 'origArrivalDateTime',
      header: 'Arrival Time',
      cell: ({ row }) => formatDateTime(row.original.origArrivalDateTime),
      sortingFn: 'datetime',
    },
    {
      accessorKey: 'dispatcherUsername',
      header: 'Dispatcher',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'container',
      header: 'Container',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'sizeType',
      header: 'Size/Type',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'triaxle',
      header: 'Triaxle',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'swing',
      header: 'Swing',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'otr',
      header: 'OTR',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'legtype',
      id: 'origType',
      header: 'Type',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'originName',
      header: 'From',
      filterFn: 'arrIncludes',
      cell: ({ row }) => (
        <div>
          <div className="text-sm">{row.original.originName || '-'}</div>
          {row.original.origCityState && (
            <div className="text-xs text-muted">{row.original.origCityState}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'legaction2',
      id: 'destType',
      header: 'Type',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'destName',
      header: 'To',
      // THIS leg's destination — a row here is a leg, not an order — where the
      // registry's `destname` is the order's delivery location. Different data, so no
      // registry title.
      meta: { registryTitle: false },
      filterFn: 'arrIncludes',
      cell: ({ row }) => (
        <div>
          <div className="text-sm">{row.original.destName || '-'}</div>
          {row.original.destCityState && (
            <div className="text-xs text-muted">{row.original.destCityState}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'sendtogwimove',
      header: 'gwiMove',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    // Hidden-by-default extras kept for power users via the column visibility menu.
    {
      accessorKey: 'legnum',
      header: 'Leg',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<number>() ?? '-',
    },
    {
      accessorKey: 'legstatus',
      header: 'Status',
      filterFn: 'arrIncludes',
      cell: ({ row }) => {
        const r = row.original;
        if (r.isComplete) return 'Complete';
        if (r.isInTransit) return 'In Transit';
        if (r.isStarted) return 'Started';
        return r.legstatus || 'Pending';
      },
    },
    {
      accessorKey: 'legdrop',
      header: 'Drop',
      filterFn: 'arrIncludes',
      cell: (info) => (info.getValue<string>() === 'Y' ? 'Yes' : 'No'),
    },
    {
      accessorKey: 'destApptDate',
      header: 'Dest Appt',
      filterFn: 'arrIncludes',
      cell: ({ row }) => {
        const d = formatDate(row.original.destApptDate);
        const t = formatTime(row.original.destApptTime1);
        return t ? `${d} ${t}` : d;
      },
      sortingFn: 'datetime',
    },
    {
      accessorKey: 'origDepartDateTime',
      header: 'Departed Orig',
      cell: ({ row }) => formatDateTime(row.original.origDepartDateTime),
      sortingFn: 'datetime',
    },
    {
      accessorKey: 'arrivalDateTime',
      header: 'Arrived Dest',
      cell: ({ row }) => formatDateTime(row.original.arrivalDateTime),
      sortingFn: 'datetime',
    },
    {
      accessorKey: 'departDateTime',
      header: 'Departed Dest',
      cell: ({ row }) => formatDateTime(row.original.departDateTime),
      sortingFn: 'datetime',
    },
    {
      accessorKey: 'legmiles',
      header: 'Miles',
      cell: (info) => {
        const v = info.getValue<number | null>();
        return v == null ? '-' : v.toLocaleString();
      },
    },
    {
      accessorKey: 'estimateddriverpay',
      header: 'Est. Pay',
      cell: (info) => {
        const v = info.getValue<number | null>();
        return v == null ? '-' : `$${v.toFixed(2)}`;
      },
    },
    {
      accessorKey: 'dispatchSentAt',
      header: 'Dispatched At',
      cell: ({ row }) => formatDateTime(row.original.dispatchSentAt),
      sortingFn: 'datetime',
    },
    {
      id: 'actions',
      header: 'Actions',
      enableColumnFilter: false,
      enableSorting: false,
      enableGrouping: false,
      size: 90,
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <DispatchPaperworkActions
            row={row.original}
            onOpenSendModal={() => setSendModalRow(row.original)}
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setTravelHistoryRow(row.original);
            }}
            className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-slate-100 dark:hover:bg-gray-700"
            aria-label="Driver travel history"
            title="Driver travel history"
          >
            <MapPinIcon className="w-4 h-4 text-slate-600 dark:text-slate-300" />
          </button>
        </div>
      ),
    },
  ], [openOrderDrawer, isViewed, markViewed, handlePrioritySave]);

  // Rows are legs of a driver's history — several can share a pro — so an added
  // order column repeats down them rather than blanking.
  const { columns, availableFields } = useAdditionalFieldColumns<DriverDispatchHistoryRow>(
    fieldSelection, { baseColumns },
  );

  const defaultColumnVisibility = useMemo(() => ({
    legnum: false,
    legstatus: false,
    legdrop: false,
    destApptDate: false,
    origDepartDateTime: false,
    arrivalDateTime: false,
    departDateTime: false,
    legmiles: false,
    estimateddriverpay: false,
    dispatchSentAt: false,
  }), []);

  const getRowId = useCallback((row: DriverDispatchHistoryRow) => {
    return `${row.pronumber}-${row.legrecno}`;
  }, []);

  return (
    <div className="flex flex-col h-full">
      <FilterBar>
        <div className="flex items-center gap-2">
          <MagnifyingGlassIcon className="h-4 w-4 text-gray-500" />
          <input
            type="text"
            value={unitInput}
            onChange={(e) => setUnitInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyUnit();
            }}
            onBlur={applyUnit}
            placeholder="Unit #"
            className="w-24 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-panel text-heading"
            aria-label="Driver unit number"
          />
          {driverName && (
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {driverName}<MuleBadge isMule={isMule} />
            </span>
          )}
        </div>
        <FilterBar.Divider />
        <div className="flex items-center gap-1">
          <span className="text-xs text-muted">Start</span>
          <DateTimePicker
            mode="date"
            selectionType="single"
            value={startDate}
            onChange={handleStartChange}
            inputVariant="compact"
            labelType="none"
            ariaLabel="Start date"
          />
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-muted">End</span>
          <DateTimePicker
            mode="date"
            selectionType="single"
            value={endDate}
            onChange={handleEndChange}
            inputVariant="compact"
            labelType="none"
            ariaLabel="End date"
          />
        </div>
        <FilterBar.Divider />
        <button
          type="button"
          onClick={handleRefresh}
          disabled={!unitParam || loading || rangeInverted}
          className="inline-flex items-center gap-1 px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 bg-panel text-heading hover:bg-slate-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
          aria-label="Refresh dispatch history"
          title="Refresh dispatch history"
        >
          <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </FilterBar>

      {error && (
        <div className="px-4 py-2 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border-b border-red-200 dark:border-red-700">
          {error}
        </div>
      )}

      {rangeInverted && (
        <div className="px-4 py-2 text-sm text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-700">
          Start date is after the end date, so no dispatches can fall in that range. Adjust either date to see results.
        </div>
      )}

      {!unitParam && (
        <div className="px-4 py-2 text-sm text-muted bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
          Enter a driver unit number above to see dispatch history.
        </div>
      )}

      <div className="flex-1 min-h-0 p-4">
        <TanTable
          data={data}
          columns={columns}
          availableFields={availableFields}
          additionalFields={fieldSelection.additionalFields}
          onAdditionalFieldsChange={fieldSelection.onAdditionalFieldsChange}
          loading={loading}
          defaultSorting={[{ id: 'origApptDate', desc: true }, { id: 'dispatch_priority', desc: false }]}
          defaultColumnVisibility={defaultColumnVisibility}
          lockedColumns={[{ id: 'actions', side: 'right' }]}
          settingsKey="driver-dispatch-history"
          enableColumnFilters={true}
          enableGrouping={true}
          getRowId={getRowId}
          enableRowSelection={true}
          canSelectRow={canSelectRow}
          onSelectionChange={setSelectedRows}
          selectionResetKey={selectionResetKey}
          getRowClassName={(row) => row.isComplete ? 'bg-green-50 dark:bg-green-900/20' : ''}
          cardTitle={
            unitParam
              ? `Driver Dispatch History — ${driverName || `Unit ${unitParam}`}${isMule ? " (Mule)" : ""}`
              : 'Driver Dispatch History'
          }
          fillContainer={true}
        />
      </div>

      <div className="shrink-0 flex justify-end border-t border-gray-200 dark:border-gray-700 bg-panel px-4 py-3">
        <button
          type="button"
          onClick={() => setConfirmSendOpen(true)}
          disabled={selectedRows.length === 0 || sendingToGwiMove}
          title={selectedRows.length === 0
            ? 'Select one or more legs to send to gwiMove'
            : `Send ${selectedRows.length} selected leg(s) to gwiMove`}
          className="inline-flex items-center gap-2 rounded px-3 py-2 text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <PaperAirplaneIcon className="h-4 w-4" />
          {selectedRows.length > 0 ? `Send to gwiMove (${selectedRows.length})` : 'Send to gwiMove'}
        </button>
      </div>

      <WarningConfirmModal
        open={confirmSendOpen}
        title="Send to gwiMove"
        message={
          <>Send <span className="font-semibold">{selectedRows.length}</span> selected move{selectedRows.length !== 1 ? 's' : ''} for {driverName || `Unit ${unitParam}`} to gwiMove?</>
        }
        confirmLabel="Send"
        cancelLabel="Cancel"
        onConfirm={handleConfirmSendToGwiMove}
        onCancel={() => setConfirmSendOpen(false)}
      />

      <SendDispatchConfirmModal
        isOpen={!!sendModalRow}
        onClose={() => setSendModalRow(null)}
        dispatchRecno={sendModalRow?.dispatchrecno ?? 0}
        pronumber={sendModalRow ? String(sendModalRow.pronumber) : ''}
        legnum={sendModalRow?.legnum ?? 0}
        driverUnit={unitParam}
      />

      <TravelHistoryModal
        isOpen={!!travelHistoryRow}
        onClose={() => setTravelHistoryRow(null)}
        proNumber={travelHistoryRow ? String(travelHistoryRow.pronumber) : ''}
        legNum={travelHistoryRow?.legnum ?? 0}
      />

      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={isOrderDrawerOpen && selectedPro !== null}
        onClose={closeOrderDrawer}
      />
    </div>
  );
}

interface DispatchPaperworkActionsProps {
  row: DriverDispatchHistoryRow;
  onOpenSendModal: () => void;
}

export const DispatchPaperworkActions = ({ row, onOpenSendModal }: DispatchPaperworkActionsProps) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const MENU_WIDTH = 224;
  const MENU_HEIGHT = 108;
  const MARGIN = 8;
  // Print → CUPS (V1 driverdispatch_print.pl). The dialog is mounted here, not
  // inside the menu, so it survives the menu closing on the click that opens it.
  const [printOpen, setPrintOpen] = useState(false);

  useEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    let top = rect.bottom + 4;
    let left = rect.left;
    if (left + MENU_WIDTH > window.innerWidth - MARGIN) {
      left = Math.max(MARGIN, window.innerWidth - MENU_WIDTH - MARGIN);
    }
    if (top + MENU_HEIGHT > window.innerHeight - MARGIN) {
      top = Math.max(MARGIN, rect.top - MENU_HEIGHT - 4);
    }
    setMenuPos({ top, left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e: MouseEvent) => {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const hasDispatch = !!row.dispatchrecno;

  const handleSend = () => {
    setOpen(false);
    if (!hasDispatch) {
      alert('This leg is not dispatched — cannot send paperwork.');
      return;
    }
    onOpenSendModal();
  };

  const handleDownload = async () => {
    setOpen(false);
    const dispatchRecno = row.dispatchrecno;
    if (!dispatchRecno) {
      alert('This leg is not dispatched — cannot download paperwork.');
      return;
    }
    try {
      await saveAs(`dispatch_${dispatchRecno}.pdf`, () =>
        dispatchPaperworkService.downloadBlob(dispatchRecno),
      );
    } catch (err) {
      console.error('Failed to download dispatch paperwork', err);
      alert('Failed to download dispatch paperwork. Check the console for details.');
    }
  };

  const handlePrint = () => {
    setOpen(false);
    if (!hasDispatch) {
      alert('This leg is not dispatched — cannot print paperwork.');
      return;
    }
    setPrintOpen(true);
  };

  const printPaperwork = (opts: Required<PrintOptions>) =>
    printingService.printTo(`/driver-dispatch/dispatch-paperwork/${row.dispatchrecno ?? 0}/print`, opts);

  return (
    <>
      {/* Mounted only while open: one dialog per row would otherwise subscribe
          every row to the print-defaults fetch and the toast context. */}
      {printOpen && (
        <PrintDialog
          open
          type="LASER"
          title="Print Paperwork"
          onClose={() => setPrintOpen(false)}
          onPrint={printPaperwork}
        />
      )}
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-slate-100 dark:hover:bg-gray-700 disabled:opacity-40"
        disabled={!hasDispatch}
        aria-label="Dispatch paperwork actions"
        aria-haspopup="menu"
        aria-expanded={open}
        title={hasDispatch ? 'Send / download dispatch paperwork' : 'No dispatch record on this leg'}
      >
        <PaperAirplaneIcon className="w-4 h-4 text-slate-600 dark:text-slate-300" />
      </button>
      {open && menuPos && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, width: MENU_WIDTH }}
          className="z-[9999] rounded-md border border-slate-200 dark:border-gray-700 bg-panel shadow-lg py-1 text-sm text-slate-700 dark:text-gray-200"
        >
          <button
            type="button"
            role="menuitem"
            onClick={handleSend}
            className="block w-full text-left px-3 py-1.5 hover:bg-slate-100 dark:hover:bg-gray-700"
          >
            Send Paperwork To Driver
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={handleDownload}
            className="block w-full text-left px-3 py-1.5 hover:bg-slate-100 dark:hover:bg-gray-700"
          >
            Download Paperwork As PDF
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={handlePrint}
            className="block w-full text-left px-3 py-1.5 hover:bg-slate-100 dark:hover:bg-gray-700"
            data-testid="dispatch-history-print"
          >
            Print Paperwork
          </button>
        </div>,
        document.body,
      )}
    </>
  );
};

function DriverDispatchHistoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const unit = searchParams.get('unit') || '';
  const start = searchParams.get('start') || todayKey();
  const end = searchParams.get('end') || todayKey();

  const setParam = useCallback((key: string, value: string | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  return (
    <ViewedProsProvider>
      <DriverDispatchHistoryContent
        unit={unit}
        startDate={start}
        endDate={end}
        onUnitChange={(u) => setParam('unit', u || null)}
        onStartChange={(d) => setParam('start', d)}
        onEndChange={(d) => setParam('end', d)}
      />
    </ViewedProsProvider>
  );
}

export interface DriverDispatchHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  unitNumber: string;
}

export function DriverDispatchHistoryModal({ isOpen, onClose, unitNumber }: DriverDispatchHistoryModalProps) {
  const [unit, setUnit] = useState(unitNumber);
  const [start, setStart] = useState<string>(() => todayKey());
  const [end, setEnd] = useState<string>(() => todayKey());

  // When the modal opens or the requested driver changes, reset filters so
  // each open behaves like a fresh launch from the search bar.
  useEffect(() => {
    if (!isOpen) return;
    setUnit(unitNumber);
    setStart(todayKey());
    setEnd(todayKey());
  }, [isOpen, unitNumber]);

  const initialWidth = Math.round(window.innerWidth * 0.9);
  const initialHeight = Math.round(window.innerHeight * 0.8);

  return (
    <DraggableModal
      isOpen={isOpen}
      onClose={onClose}
      title="Driver Dispatch History"
      width={initialWidth}
      initialHeight={initialHeight}
      resizable
      minWidth={520}
      minHeight={360}
      contentClassName="flex-1 min-h-0 flex flex-col overflow-hidden"
      persistKey="driver_dispatch_history"
    >
      <ViewedProsProvider>
        <DriverDispatchHistoryContent
          unit={unit}
          startDate={start}
          endDate={end}
          onUnitChange={setUnit}
          onStartChange={setStart}
          onEndChange={setEnd}
        />
      </ViewedProsProvider>
    </DraggableModal>
  );
}

export default DriverDispatchHistoryPage;
